import type { FC } from "react";
import {
  PersonifySsoControllerService,
  TranscarentSsoControllerService
} from "@9amhealth/openapi";
import type { SuccessLoginResponse } from "@9amhealth/openapi";
import { AuthenticationMethod } from "src/constants/authenticationMethod";
import { PayerId } from "src/constants/payers";
import FunnelKey from "src/constants/funnelKey";
import { KnownPartnerSources } from "src/constants/partner";
import { IconPersonifyHealth, IconTranscarent } from "src/constants/icons";
import type { KnownDynamicFlags } from "src/lib/featureFlags";
import { getContinueWithTranscarentTargetUrl } from "src/lib/getContinueWithTranscarentTargetUrl";
import { getContinueWithPersonifyTargetUrl } from "src/lib/getContinueWithPersonifyTargetUrl";
import { isHybridApp } from "src/lib/platform";

export interface PartnerConfig {
  payerId: PayerId;
  funnelKey: FunnelKey;
  /** The auth method a real SSO session from this partner carries. */
  ssoAuthMethod: AuthenticationMethod;
  /** Session-auth methods that satisfy this partner's payer-gated reauth check. */
  acceptedAuthMethods: AuthenticationMethod[];
  /**
   * True when 9am is loaded inside the partner's own in-app webview (e.g.
   * Transcarent's `TCMobileInAppBrowser`), which drives webview-chrome UI
   * changes (tab-style nav, hidden chrome). Personify is NOT embedded — the
   * member reaches 9am as a normal browser/app session.
   */
  isEmbeddedWebview: boolean;
  /** Only set when isEmbeddedWebview — substring matched against navigator.userAgent. */
  embeddedWebviewUserAgentSubstring?: string;
  /** Matched against the `?from=` query param on landing. */
  detectQueryParam?: string;
  /** Additive `html` class (alongside `partner-session`) that scopes this partner's brand CSS vars. */
  cssClassName: string;
  icon: FC;
  /** Member-facing partner name, interpolated into shared partner copy. */
  displayName: string;
  /** i18next context suffix for `login`/`login.subtitle` translation keys. */
  loginTranslateContext: string;
  ssoLoginFeatureFlag: KnownDynamicFlags;
  getContinueUrl: () => string | undefined;
  /**
   * When true, every route of this partner's funnel shows a "connect your
   * account" screen leading to `getContinueUrl()` unless the session was
   * authenticated by this partner — Personify members always arrive via SSO,
   * and that SSO presence is their pre-signup eligibility check.
   */
  requireSsoOnEntry?: boolean;
  /**
   * When true, "Continue with…" on web replaces the current tab instead of
   * opening a new one. Personify's continue-URL is our own `/sso/start`, which
   * round-trips back into 9am — a new tab would strand the original one on
   * the login screen.
   */
  continueInSameTab?: boolean;
  /**
   * The JWT `type` claim this partner's single-use SSO exchange token carries
   * (backend `JwtTokenClaims.TokenType`, e.g. `TRANSCARENT_SSO_EXCHANGE`).
   * `HandleOneTimeToken` decodes an incoming `/auth/ott` token and looks up
   * its partner by this claim, since both partners share that one landing
   * route and the token itself is the only signal available to tell them
   * apart (see `getPartnerConfigByExchangeTokenType`).
   */
  exchangeTokenType: string;
  /** Exchanges this partner's single-use SSO token for a 9am session. */
  exchangeToken: (token: string) => Promise<SuccessLoginResponse>;
}

export const PARTNER_CONFIGS: Partial<
  Record<KnownPartnerSources, PartnerConfig>
> = {
  [KnownPartnerSources.transcarent]: {
    payerId: PayerId.TRANSCARENT,
    funnelKey: FunnelKey.transcarent,
    ssoAuthMethod: AuthenticationMethod.TRANSCARENT_SSO,
    acceptedAuthMethods: [
      AuthenticationMethod.SAML2,
      AuthenticationMethod.TRANSCARENT_SSO
    ],
    isEmbeddedWebview: true,
    embeddedWebviewUserAgentSubstring: "TCMobileInAppBrowser",
    detectQueryParam: "tc-ott",
    cssClassName: "partner-source-transcarent",
    icon: IconTranscarent,
    displayName: "Transcarent",
    loginTranslateContext: "transcarent",
    ssoLoginFeatureFlag: "sso_login_option_transcarent",
    getContinueUrl: getContinueWithTranscarentTargetUrl,
    exchangeTokenType: "TRANSCARENT_SSO_EXCHANGE",
    exchangeToken: (token) =>
      TranscarentSsoControllerService.exchangeToken({ token })
  },
  [KnownPartnerSources.personifyHealth]: {
    payerId: PayerId.PERSONIFY_HEALTH,
    funnelKey: FunnelKey.personifyHealth,
    ssoAuthMethod: AuthenticationMethod.PERSONIFY_SSO,
    acceptedAuthMethods: [AuthenticationMethod.PERSONIFY_SSO],
    // Personify does not embed 9am in a webview — no UA substring, so
    // detection never sets webview-chrome UI (mobileTabNavigation) for it.
    isEmbeddedWebview: false,
    detectQueryParam: "ph-ott",
    cssClassName: "partner-source-personify-health",
    icon: IconPersonifyHealth,
    displayName: "Personify Health",
    loginTranslateContext: "personify_health",
    ssoLoginFeatureFlag: "sso_login_option_personify_health",
    getContinueUrl: getContinueWithPersonifyTargetUrl,
    requireSsoOnEntry: true,
    continueInSameTab: true,
    exchangeTokenType: "PERSONIFY_SSO_EXCHANGE",
    exchangeToken: (token) =>
      PersonifySsoControllerService.exchangePersonifySsoToken({ token })
  }
};

/** Opens a partner's SSO continue-URL, exactly as its "Continue with…" button does. */
export const openPartnerContinueUrl = (config: PartnerConfig): void => {
  if (config.continueInSameTab) {
    navigateToPartnerContinueUrl(config);
    return;
  }

  const target = config.getContinueUrl();

  if (!target) {
    return;
  }

  window.open(target, isHybridApp() ? "_blank" : undefined);
};

/**
 * Hard-navigates the current tab to a partner's SSO continue-URL on web.
 * Hybrid apps still open "_blank", which escapes into the system browser
 * rather than navigating the app's own webview away.
 */
export const navigateToPartnerContinueUrl = (config: PartnerConfig): void => {
  const target = config.getContinueUrl();

  if (!target) {
    return;
  }

  if (isHybridApp()) {
    window.open(target, "_blank");
    return;
  }

  window.location.href = target;
};

/** Finds the partner config whose `funnelKey` matches, if any. */
export const getPartnerConfigByFunnelKey = (
  funnelKey?: string
): PartnerConfig | undefined =>
  Object.values(PARTNER_CONFIGS).find(
    (config) => config.funnelKey === funnelKey
  );

export type PartnerEntryGate = "none" | "pending" | "connect";

/**
 * Whether a funnel route must send the member to the partner's SSO first
 * (via the "connect your account" screen). A session the partner already
 * authenticated must stay in the funnel — after the SSO handover the app
 * routes the member straight back into it, so gating unconditionally loops
 * through the IdP forever.
 */
export const resolvePartnerEntryGate = ({
  funnelKey,
  sessionRestoreSettled,
  isAuthenticated,
  userDataLoaded,
  sessionAuthenticationMethod
}: {
  funnelKey?: string;
  sessionRestoreSettled: boolean;
  isAuthenticated: boolean;
  userDataLoaded: boolean;
  sessionAuthenticationMethod?: string;
}): PartnerEntryGate => {
  const partner = getPartnerConfigByFunnelKey(funnelKey);

  if (!partner?.requireSsoOnEntry) {
    return "none";
  }

  if (!sessionRestoreSettled || (isAuthenticated && !userDataLoaded)) {
    return "pending";
  }

  const isPartnerSession =
    isAuthenticated &&
    partner.acceptedAuthMethods.includes(
      sessionAuthenticationMethod as AuthenticationMethod
    );

  return isPartnerSession ? "none" : "connect";
};

/**
 * Finds the partner whose SSO exchange token carries the given JWT `type`
 * claim. `/auth/ott` is shared by every partner's exchange-token handover
 * (backend redirects there with just `?token=`, no partner marker), so the
 * token's own declared type is the only reliable way to route it — see
 * `PartnerConfig.exchangeTokenType`.
 */
export const getPartnerConfigByExchangeTokenType = (
  tokenType?: string
): PartnerConfig | undefined =>
  Object.values(PARTNER_CONFIGS).find(
    (config) => config.exchangeTokenType === tokenType
  );

/**
 * True if `method` is a partner's own dedicated SSO auth method (e.g.
 * TRANSCARENT_SSO, PERSONIFY_SSO) — NOT the generic SAML2 method, even though
 * SAML2 also satisfies some partners' `acceptedAuthMethods`. Used for
 * identity-field locking, which is only appropriate when the SSO exchange
 * itself supplied the identity data (the dedicated OTT/exchange flows), not
 * for a generic SAML2 login where 9am has no such guarantee.
 */
export const isPartnerPrimarySsoMethod = (method?: string): boolean =>
  Object.values(PARTNER_CONFIGS).some(
    (config) => config.ssoAuthMethod === method
  );

/**
 * True if `method` satisfies ANY partner's accepted-auth-methods list
 * (includes generic SAML2 where a partner accepts it). Used where the caller
 * only cares "is this session already SSO-confirmed by some partner",
 * regardless of which one.
 */
export const isAnyPartnerAcceptedAuthMethod = (method?: string): boolean =>
  Object.values(PARTNER_CONFIGS).some((config) =>
    config.acceptedAuthMethods.includes(method as AuthenticationMethod)
  );

/** Finds the partner config whose `payerId` matches, if any. */
export const getPartnerConfigByPayerId = (
  payerId?: string
): PartnerConfig | undefined =>
  Object.values(PARTNER_CONFIGS).find((config) => config.payerId === payerId);

/** Finds the partner key whose `payerId` matches, if any. */
export const getPartnerSourceByPayerId = (
  payerId?: string
): KnownPartnerSources | undefined => {
  const entry = Object.entries(PARTNER_CONFIGS).find(
    ([, config]) => config.payerId === payerId
  );
  return entry?.[0] as KnownPartnerSources | undefined;
};

/**
 * Embedded partners (Transcarent) own the session inside their app, so 9am
 * hides logout there; a partner that isn't embedded (Personify) needs it.
 * Unknown sources keep it hidden, as before partner configs existed.
 */
export const canLogOutOfPartnerSession = (
  source?: KnownPartnerSources
): boolean =>
  source !== undefined && PARTNER_CONFIGS[source]?.isEmbeddedWebview === false;
